import { ReactNode } from "react";
import styled from "styled-components";

import { useIsPhoneLayout } from "@pages/AdminDashboard/theme/AdminLayout";
import { adminFontFamily, adminTokens } from "@pages/AdminDashboard/theme/adminTokens";

const { color, radius, shadow, typeScale } = adminTokens;
const [, spaceSm, spaceMd, spaceLg, , spaceXl] = adminTokens.spacing;

// The design sets a card title in Light 24 (CARD-R7); it is not one of the
// eight fixed text styles yet, so the size stays here until that is decided.
const cardTitleSize = "24px";
const cardTitleLineHeight = 1.25;

// On a phone a card pads 16px, the design's phone content padding (PHONE-R28).
const Surface = styled.div<{ $isPhone: boolean }>`
  display: flex;
  flex-direction: column;
  gap: ${spaceLg}px;
  box-sizing: border-box;
  min-width: 0;
  background-color: ${color.surface};
  border: 1px solid ${color.border};
  border-radius: ${radius.card}px;
  box-shadow: ${shadow.card};
  padding: ${({ $isPhone }) => ($isPhone ? spaceLg : spaceXl)}px;
  color: ${color.navy};
  font-family: ${typeScale.body.fontFamily};
  font-size: ${typeScale.body.fontSize};
  line-height: ${typeScale.body.lineHeight};
`;

// On a phone the right-side content moves below the title when it does not fit beside it
// (PHONE-R41).
const Header = styled.div<{ $isPhone: boolean }>`
  display: flex;
  flex-direction: row;
  flex-wrap: ${({ $isPhone }) => ($isPhone ? "wrap" : "nowrap")};
  align-items: flex-start;
  justify-content: space-between;
  gap: ${({ $isPhone }) => ($isPhone ? spaceSm : spaceMd)}px;
`;

const Titles = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const Eyebrow = styled.span`
  color: ${color.inkSoft};
  font-family: ${typeScale.eyebrow.fontFamily};
  font-size: ${typeScale.eyebrow.fontSize};
  letter-spacing: ${typeScale.eyebrow.letterSpacing};
  text-transform: ${typeScale.eyebrow.textTransform};
`;

const Title = styled.h2`
  margin: 0;
  font-family: ${adminFontFamily.light};
  font-size: ${cardTitleSize};
  font-weight: normal;
  line-height: ${cardTitleLineHeight};
  overflow-wrap: anywhere;
`;

const Aside = styled.div`
  display: flex;
  align-items: center;
  gap: ${spaceSm}px;
  flex: none;
`;

interface CardProps {
  eyebrow?: string;
  title?: string;
  titleLevel?: 2 | 3;
  aside?: ReactNode;
  children?: ReactNode;
}

// The white surface every admin screen groups its content on (CARD-R1…R6). The
// header appears only when the screen gives it something to show.
export const Card = ({ eyebrow, title, titleLevel = 2, aside, children }: CardProps) => {
  const isPhone = useIsPhoneLayout();
  const hasHeader = Boolean(eyebrow || title || aside);

  return (
    <Surface $isPhone={isPhone}>
      {hasHeader && (
        <Header $isPhone={isPhone}>
          <Titles>
            {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
            {title && <Title as={titleLevel === 3 ? "h3" : "h2"}>{title}</Title>}
          </Titles>
          {aside && <Aside>{aside}</Aside>}
        </Header>
      )}
      {children}
    </Surface>
  );
};
